import { useEffect, useState } from 'react'
import { ParsedUrlQuery } from 'querystring'
import {
  FieldConfiguration,
  OptionSelection,
  PreloadConfiguration,
  ProductVariant,
  PersistedConfiguration,
} from '../types'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import uniqBy from 'lodash/uniqBy'
import { loadPersistedUploadConfiguration } from './utils'
import { systemProperties } from '../constants'
import {
  LEGACY_EDITOR_URL_FIELDS,
  loadOldV3EditorConfigFromLocalStorage,
} from '../../../_utils/legacyEditors.utils'
import { DESIGN_PATHS } from '../../constants'
import _ from 'lodash'
import { invalidFileUrlPrefix } from '@utils/utilities'

function toPreloadConfiguration(
  configurationLoaded: PersistedConfiguration,
  options: OptionSelection[],
  productVariantsSales: ProductVariant[],
  itemId: string
): PreloadConfiguration {
  const variantId = _.get(
    configurationLoaded,
    'variantId',
    _.get(configurationLoaded, 'selectedProduct.variantId', null)
  )
  const variantSku = _.get(configurationLoaded, 'variantSku', null)

  const designJourney = configurationLoaded.designJourney
  // Looking it up from product options list
  const productOption = productVariantsSales.find(
    (productOption) =>
      String(productOption.variantSku) === String(variantSku) ||
      String(productOption.variantId) === String(variantId)
  )

  if (!isEmpty(productOption)) {
    // Product selected to option
    const filteredProductOption = omit(productOption, systemProperties)
    const optionsFromConfig = Object.keys(filteredProductOption).map(
      (prop) => ({
        name: prop,
        value: filteredProductOption[prop],
      })
    )
    const filesUrls = _.chain(LEGACY_EDITOR_URL_FIELDS)
      .map((field) => _.get(configurationLoaded, field, []))
      .concat(_.get(configurationLoaded, 'fullpath'))
      .flatten()
      .filter((url) => !_.isNil(url) && !_.isEmpty(url))
      .filter(
        (url) =>
          !url.startsWith(invalidFileUrlPrefix) &&
          (url.indexOf('/upload_attachments/') > 0 ||
            url.indexOf('media/upload_') > 0)
      )
      .uniq()
      .value()

    return {
      options: uniqBy([...options, ...optionsFromConfig], 'name'),
      filesUrls,
      ...(itemId ? { itemId } : {}),
      designJourney: designJourney,
      version: configurationLoaded.version as 'V1' | 'V2' | 'V3',
    }
  }

  // No option found. ItemId doesn't make sense to include here
  return {
    options: [],
    filesUrls: [],
    designJourney: DESIGN_PATHS.DESIGN_UPLOAD,
  }
}

export default function usePreloadEditorConfiguration({
  fieldsVisibilityConfiguration,
  query,
  productVariantsSales,
}: {
  fieldsVisibilityConfiguration: FieldConfiguration[]
  query: ParsedUrlQuery
  productVariantsSales: ProductVariant[]
}) {
  const [loading, setLoading] = useState(false)
  const [configuration, setConfiguration] = useState({
    options: [],
    filesUrls: [],
    brief: {},
  } as PreloadConfiguration)

  useEffect(() => {
    const loadConfiguration = async () => {
      // Extract selection options from query params first
      const options = []

      for (const field of fieldsVisibilityConfiguration) {
        if (query[field.name]) {
          options.push({
            name: field.name,
            value: query[field.name],
          })
        }
      }

      let itemId = null

      if (query?.edit) {
        itemId = query?.itemId
      }

      if (query?.configId) {
        setLoading(true)
        // Then load it from configuration id if it has
        // Load config
        const configId = query?.configId as string

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        const configurationLoaded = await loadPersistedUploadConfiguration(
          configId
        )

        if (isEmpty(configurationLoaded)) {
          return
        }
        const mergedConfiguration = toPreloadConfiguration(
          configurationLoaded,
          options,
          productVariantsSales,
          itemId
        )
        let editorVersion = configurationLoaded?.version
        // some persisted configurations are missing the version field
        if (!editorVersion) {
          const editorName = _.get(configurationLoaded, 'editorName')
          const workflow = _.get(configurationLoaded, 'workflow')
          const templateId = _.get(configurationLoaded, 'templateId')
          if (editorName === 'community-design-editor') {
            editorVersion = 'V3'
          } else if (workflow === 'design-online' && !_.isEmpty(templateId)) {
            editorVersion = 'V2'
          } else {
            editorVersion = 'V1'
          }
        }
        setConfiguration({
          ...mergedConfiguration,
          version: editorVersion as 'V1' | 'V2' | 'V3',
          brief: configurationLoaded?.brief || null,
          onlineEditor: configurationLoaded?.onlineEditor || null,
          designJourney: configurationLoaded?.designJourney || null,
          qrCodeChoice: configurationLoaded?.qrCodeChoice || null,
        })
        setLoading(false)
        return
      } else {
        const savedConfigFromLocalStorage =
          loadOldV3EditorConfigFromLocalStorage(itemId)

        if (isEmpty(savedConfigFromLocalStorage)) {
          setConfiguration({
            options,
            filesUrls: [],
          })
          setLoading(false)
          return
        }
        setConfiguration({
          options,
          designJourney: savedConfigFromLocalStorage?.designJourney,
          filesUrls: savedConfigFromLocalStorage?.filesUrls,
        })
        setLoading(false)
      }
    }

    if (!isEmpty(query)) {
      loadConfiguration()
    }
  }, [query])

  return {
    preloadConfiguration: configuration as PreloadConfiguration,
    configurationIsLoading: loading,
  }
}
